import React from 'react';
import { motion } from 'framer-motion';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme } from '../context/ThemeContext';
import {
  SiReact, SiNextdotjs, SiJavascript, SiTypescript, SiHtml5, SiCss3,
  SiTailwindcss, SiNodedotjs, SiExpress, SiNestjs, SiPhp, SiLaravel,
  SiGraphql, SiPostgresql, SiMysql, SiMongodb, SiRedis, SiDocker,
  SiAmazonwebservices, SiNginx, SiLinux, SiGit
} from 'react-icons/si';
import { FiServer, FiGitBranch, FiCode } from 'react-icons/fi';
import { skills } from '../data/portfolioData';

const getSkillIcon = (iconName) => {
  const icons = {
    react: SiReact,
    nextjs: SiNextdotjs,
    javascript: SiJavascript,
    typescript: SiTypescript,
    html5: SiHtml5,
    css3: SiCss3,
    tailwindcss: SiTailwindcss,
    nodejs: SiNodedotjs,
    express: SiExpress,
    nestjs: SiNestjs,
    php: SiPhp,
    laravel: SiLaravel,
    api: FiServer,
    graphql: SiGraphql,
    postgresql: SiPostgresql,
    mysql: SiMysql,
    mongodb: SiMongodb,
    redis: SiRedis,
    docker: SiDocker,
    aws: SiAmazonwebservices,
    cicd: FiGitBranch,
    nginx: SiNginx,
    linux: SiLinux,
    git: SiGit,
  };
  const Icon = icons[iconName] || FiCode;
  return <Icon />;
};

const importCode = `import { React, NextJS, TypeScript } from 'frontend';
import { NodeJS, NestJS, Laravel } from 'backend';
import { PostgreSQL, MongoDB, Redis } from 'database';
import { Docker, AWS, Git } from 'devops';`;

const categories = [
  { key: 'frontend', title: 'Frontend', color: '#61dafb' },
  { key: 'backend', title: 'Backend', color: '#68a063' },
  { key: 'database', title: 'Database', color: '#336791' },
  { key: 'devops', title: 'DevOps & Cloud', color: '#ff9900' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3 },
  },
};

const Skills = () => {
  const { theme } = useTheme();
  return (
    <section id="skills" className="skills">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <motion.h2 className="section-comment" variants={itemVariants}>
          {'// tech-stack'}
        </motion.h2>
        <motion.p className="section-subtitle" variants={itemVariants}>
          Technologies I work with daily to build robust applications
        </motion.p>

        <motion.div className="skills-imports" variants={itemVariants}>
          <SyntaxHighlighter
            language="javascript"
            style={theme === 'dark' ? vscDarkPlus : oneLight}
            showLineNumbers={true}
            customStyle={{ background: 'transparent', padding: '1.5rem' }}
          >
            {importCode}
          </SyntaxHighlighter>
        </motion.div>

        <div className="skills-grid">
          {categories.map((category) => (
            <motion.div
              key={category.key}
              className="skill-category"
              variants={itemVariants}
            >
              <h3 className="category-title">
                <span
                  className="category-dot"
                  style={{ backgroundColor: category.color }}
                />
                {category.title}
              </h3>
              <motion.div
                className="skill-cards"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                {skills[category.key]?.map((skill) => (
                  <motion.div
                    key={skill.name}
                    className="skill-card"
                    variants={cardVariants}
                    whileHover={{ scale: 1.05, y: -4 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <span className="skill-icon">{getSkillIcon(skill.icon)}</span>
                    <span className="skill-name">{skill.name}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Skills;
